import { Trans, useLingui } from '@lingui/react/macro';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, useChannelStore, useDmStore, usePresenceStore } from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import { useCan } from '../hooks/usePermissions';
import { routes } from '../lib/routes';
import { useGuildId } from '../hooks/useGuildId';
import { useProjectAuthority } from '../hooks/useProjectAuthority';
import { runUnsavedGuards } from '../hooks/useUnsavedChangesGuard';
import ChannelList from './ChannelList';
import { AppstoreAddOutlined, PlusOutlined } from '@ant-design/icons';
import type { DmChannel } from '../types';
import type { AgentRuntimeReason, AgentRuntimeState, ChannelMember } from '../lib/api';
import PresenceDot from './PresenceDot';
import { UserAvatar } from './UserAvatar';
import { UserName } from './UserName';
import { usePresence } from '../hooks/usePresence';
import { useServerStore } from '../stores/useServerStore';
import { CreateChannelDialog } from './CreateChannelDialog';
import { ZoneManagementDialog } from './ZoneManagementDialog';
import styles from './ChannelRail.module.css';
import { useChannelMembers } from '../hooks/useChannelMembers';

export default function Sidebar({
  serverOnly = false,
  onTaskClick,
  activeThreadId,
  activeChannelId,
}: {
  serverOnly?: boolean;
  onTaskClick?: (threadId: string) => void;
  activeThreadId?: string;
  activeChannelId?: string | null;
}) {
  const { t, i18n } = useLingui();
  const channels = useChannelStore((s) => s.channels);
  const zones = useChannelStore((s) => s.zones);
  const loading = useChannelStore((s) => s.loading);
  const loadError = useChannelStore((s) => s.loadError);
  const loadChannels = useChannelStore((s) => s.loadChannels);
  const currentChannelId = useChannelStore((s) => s.currentChannelId);
  const dmChannels = useDmStore((s) => s.dmChannels);
  const loadDmChannels = useDmStore((s) => s.loadDmChannels);
  const openDm = useDmStore((s) => s.openDm);
  const currentUser = useAuthStore((s) => s.currentUser);
  const onlineUserIds = usePresenceStore((s) => s.onlineUserIds);
  const tasksById = useTaskStore((s) => s.byTaskId);
  const navigate = useNavigate();
  const location = useLocation();
  const guildId = useGuildId();
  const isDmMode = !serverOnly && location.pathname.startsWith('/dm');
  const hasCreateCapability = useCan('channel.create');
  const snapshotGuildId = useAuthStore((s) => s.guildId);
  const activeGuild = useServerStore((s) => s.guilds.find((guild) => guild.id === guildId));
  const scopeReady = snapshotGuildId === guildId;
  const canCreateChannel = scopeReady && Boolean(activeGuild) && hasCreateCapability;
  const authority = useProjectAuthority(guildId);
  const canManageZones = authority.ready && authority.canManageProject;
  const [createPlacement, setCreatePlacement] = useState<{ parentId: string | null } | null>(null);
  const [zoneDialog, setZoneDialog] = useState<{
    mode: 'create' | 'rename' | 'delete';
    zoneId?: string;
  } | null>(null);
  const { members: channelMembers } = useChannelMembers(currentChannelId);

  useEffect(() => {
    loadDmChannels();
  }, [loadDmChannels]);

  useEffect(() => {
    if (!isDmMode && guildId)
      void useTaskStore
        .getState()
        .loadGuildTasks(guildId)
        .catch(() => {});
  }, [guildId, isDmMode]);

  const handleSelect = (channelId: string) => {
    if (!runUnsavedGuards(undefined, i18n)) return;
    navigate(routes.channel(guildId, channelId));
  };

  const handleSelectDm = (dmChannelId: string) => {
    if (!runUnsavedGuards(undefined, i18n)) return;
    navigate(routes.dm(dmChannelId));
  };

  useEffect(() => {
    setCreatePlacement(null);
    setZoneDialog(null);
  }, [guildId, currentUser?.id]);

  useEffect(() => {
    if (!canManageZones) setZoneDialog(null);
  }, [canManageZones]);

  const nonDmChannels = useMemo(
    () => channels.filter((channel) => channel.channel_type === 0),
    [channels],
  );
  const activeTasks = useMemo(
    () =>
      Object.values(tasksById).filter(
        (task) =>
          task.guild_id === guildId &&
          (task.status === 'in_progress' || task.status === 'in_review') &&
          nonDmChannels.some(
            (channel) => channel.id === task.channel_id && channel.is_member !== false,
          ),
      ),
    [guildId, nonDmChannels, tasksById],
  );

  const sortedDms = [...dmChannels].sort((a, b) => {
    const aTime = a.last_message?.created_at ?? a.created_at;
    const bTime = b.last_message?.created_at ?? b.created_at;
    return bTime - aTime;
  });

  return (
    <div className="sidebar">
      {isDmMode ? (
        <>
          <div className="sidebar-header">
            <span className="sidebar-title">
              <Trans>Direct Messages</Trans>
            </span>
          </div>
          <MergedDmList
            dms={sortedDms}
            currentChannelId={activeChannelId ?? null}
            onlineUserIds={onlineUserIds}
            users={channelMembers}
            currentUserId={currentUser?.id}
            onSelectDm={handleSelectDm}
            onOpenDm={async (userId: string) => {
              const dm = await openDm(userId);
              navigate(routes.dm(dm.id));
            }}
          />
        </>
      ) : (
        <>
          <div className={`sidebar-header${serverOnly ? ' sidebar-header-section' : ''}`}>
            <span className="sidebar-title">
              <Trans>Channels</Trans>
            </span>
            <div className="sidebar-actions">
              {canCreateChannel && (
                <button
                  className="icon-btn"
                  onClick={() => setCreatePlacement({ parentId: null })}
                  title={t`Create Channel`}
                  aria-label={t`Create channel`}
                  type="button"
                >
                  <PlusOutlined aria-hidden="true" />
                </button>
              )}
              {canManageZones && (
                <button
                  type="button"
                  className="icon-btn"
                  title={t`Create Zone`}
                  aria-label={t`Create Zone`}
                  onClick={() => setZoneDialog({ mode: 'create' })}
                >
                  <AppstoreAddOutlined aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          {!scopeReady || (loading && channels.length === 0 && zones.length === 0) ? (
            <div className={styles.loadState} role="status">
              <Trans>Loading channels and Zones…</Trans>
            </div>
          ) : (
            <>
              {loadError && (
                <div className={styles.loadState} role="alert">
                  <p>{loadError.map((message) => i18n._(message)).join(' ')}</p>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={loading}
                    onClick={() =>
                      void loadChannels(guildId).catch(() => {
                        /* The store exposes the authoritative read error above. */
                      })
                    }
                  >
                    <Trans>Retry</Trans>
                  </button>
                </div>
              )}
              {currentUser && (!loadError || channels.length > 0 || zones.length > 0) ? (
                <ChannelList
                  channels={nonDmChannels}
                  zones={zones}
                  accountId={currentUser.id}
                  guildId={guildId}
                  currentChannelId={activeChannelId ?? null}
                  activeTasks={activeTasks}
                  activeThreadId={activeThreadId}
                  onSelectChannel={handleSelect}
                  onSelectTask={(task) => {
                    if (task.thread_id) onTaskClick?.(task.thread_id);
                  }}
                  onShowAllTasks={(channelId) =>
                    navigate(`${routes.tasks(guildId)}?channel=${encodeURIComponent(channelId)}`)
                  }
                  onCreateChannel={
                    canCreateChannel ? (parentId) => setCreatePlacement({ parentId }) : undefined
                  }
                  onManageZone={
                    canManageZones ? (zoneId, mode) => setZoneDialog({ zoneId, mode }) : undefined
                  }
                />
              ) : null}
            </>
          )}
          {createPlacement && currentUser && scopeReady && (
            <CreateChannelDialog
              key={JSON.stringify([currentUser.id, guildId])}
              guildId={guildId}
              initialParentId={createPlacement.parentId}
              members={channelMembers}
              currentUserId={currentUser.id}
              onClose={() => setCreatePlacement(null)}
              onCreated={(channel) => {
                setCreatePlacement(null);
                navigate(routes.channel(guildId, channel.id));
              }}
            />
          )}
          {zoneDialog && canManageZones && (
            <ZoneManagementDialog
              open
              mode={zoneDialog.mode}
              initialZoneId={zoneDialog.zoneId}
              onClose={() => setZoneDialog(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

function DmItem({
  dm,
  active,
  online,
  onClick,
}: {
  dm: DmChannel;
  active: boolean;
  online: boolean;
  onClick: () => void;
}) {
  const { t, i18n } = useLingui();
  const peerName = dm.peer?.display_name ?? dm.name ?? t`DM`;
  const isAgent = dm.peer?.role === 'agent';

  return (
    <button
      className={`channel-item ${active ? 'channel-item-active' : ''}`}
      onClick={onClick}
      data-role={dm.peer?.role ?? 'user'}
    >
      <UserAvatar userId={dm.peer?.id ?? dm.id} displayName={peerName} size={28} />
      <span className="channel-name">
        <UserName displayName={peerName} role={dm.peer?.role} />
      </span>
      {isAgent && dm.peer?.id && (
        <DmPresence
          agentID={dm.peer.id}
          online={online}
          state={dm.peer.state}
          reason={dm.peer.reason}
        />
      )}
      {dm.unread_count > 0 && (
        <span className="unread-badge">{dm.unread_count > 99 ? '99+' : dm.unread_count}</span>
      )}
    </button>
  );
}

function DmPresence({
  agentID,
  online,
  state: stateProp,
  reason: reasonProp,
}: {
  agentID: string;
  online: boolean;
  state?: AgentRuntimeState;
  reason?: AgentRuntimeReason;
}) {
  const live = usePresence(agentID);
  const state = live?.state ?? stateProp ?? (online ? 'online' : undefined);
  const reason = live?.reason ?? (state === 'error' ? reasonProp : undefined);
  return <PresenceDot state={state} reason={reason} compact />;
}

function MergedDmList({
  dms,
  currentChannelId,
  onlineUserIds,
  users,
  currentUserId,
  onSelectDm,
  onOpenDm,
}: {
  dms: DmChannel[];
  currentChannelId: string | null;
  onlineUserIds: Record<string, boolean>;
  users: ChannelMember[];
  currentUserId?: string;
  onSelectDm: (id: string) => void;
  onOpenDm: (userId: string) => void;
}) {
  const { t, i18n } = useLingui();
  const validDms = dms.filter((dm) => dm.peer?.id);
  const dmPeerIds = new Set(validDms.map((dm) => dm.peer.id));
  const availableMembers = users.filter(
    (u) => u.user_id !== currentUserId && !dmPeerIds.has(u.user_id),
  );

  if (validDms.length === 0 && availableMembers.length === 0) return null;

  return (
    <div className="dm-list" data-kind="dm">
      <div className="online-header" data-kind="dm">
        <Trans>Direct Messages</Trans>
      </div>
      {validDms.map((dm) => (
        <DmItem
          key={dm.id}
          dm={dm}
          active={dm.id === currentChannelId}
          online={!!onlineUserIds[dm.peer.id]}
          onClick={() => onSelectDm(dm.id)}
        />
      ))}
      {availableMembers.map((user) => (
        <button
          key={user.user_id}
          className="channel-item online-only-item"
          onClick={() => onOpenDm(user.user_id)}
          title={t`Message ${user.display_name}`}
        >
          <UserAvatar userId={user.user_id} displayName={user.display_name} size={28} />
          <span className="channel-name">
            <UserName displayName={user.display_name} role={user.role} />
          </span>
        </button>
      ))}
    </div>
  );
}
